import { useState, useEffect, useRef } from 'react';

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return { ref, inView };
}

// 本のページをめくるような曲線区切り
function CurvedDivider({ fromColor, toColor, flip = false }: { fromColor: string; toColor: string; flip?: boolean }) {
  return (
    <div className="relative w-full overflow-hidden" style={{ height: '70px', background: fromColor }}>
      <svg
        viewBox="0 0 1440 70"
        className="absolute bottom-0 w-full"
        style={{ display: 'block' }}
        preserveAspectRatio="none"
        height="70"
      >
        {flip ? (
          <path
            d="M0,0 Q360,70 720,40 Q1080,10 1440,60 L1440,70 L0,70 Z"
            fill={toColor}
          />
        ) : (
          <path
            d="M0,50 Q360,0 720,35 Q1080,70 1440,20 L1440,70 L0,70 Z"
            fill={toColor}
          />
        )}
      </svg>
    </div>
  );
}

const LINE_URL = 'https://s.lmes.jp/landing-qr/2006797040-LZWyQBdP?uLand=r3lUUz';
const LINE_NOTE = '※登録後に届く合言葉を購入時の応援コメントに入力で、商品発送時に2包プレゼント🎁';

// CTA ボタン共通コンポーネント
function LineCtaButton({ variant = 'green' }: { variant?: 'green' | 'white' }) {
  const [pressed, setPressed] = useState(false);
  const [hovered, setHovered] = useState(false);

  const base =
    'w-full flex items-center justify-center gap-2 font-black py-4 px-6 rounded-full text-sm whitespace-nowrap cursor-pointer select-none relative overflow-hidden';

  const colors =
    variant === 'white'
      ? 'bg-white text-[#e85a2a]'
      : 'bg-[#e85a2a] text-white';

  return (
    <div className="relative w-full">
      {/* パルスリング */}
      <span
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{
          boxShadow: variant === 'white'
            ? '0 0 0 0 rgba(255,255,255,0.7)'
            : '0 0 0 0 rgba(232,90,42,0.5)',
          animation: 'ctaPulse 2s ease-out infinite',
        }}
      />
      <a
        href={LINE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={`${base} ${colors}`}
        style={{
          boxShadow: pressed
            ? '0 2px 8px rgba(232,90,42,0.25)'
            : hovered
            ? variant === 'white'
              ? '0 8px 32px rgba(255,255,255,0.5), 0 0 20px rgba(255,255,255,0.3)'
              : '0 8px 32px rgba(232,90,42,0.5), 0 0 20px rgba(232,90,42,0.3)'
            : '0 6px 24px rgba(232,90,42,0.35)',
          transform: pressed ? 'scale(0.96)' : hovered ? 'scale(1.03)' : 'scale(1)',
          transition: 'transform 0.18s cubic-bezier(.4,2,.6,1), box-shadow 0.25s ease',
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => { setHovered(false); setPressed(false); }}
        onMouseDown={() => setPressed(true)}
        onMouseUp={() => setPressed(false)}
        onTouchStart={() => setPressed(true)}
        onTouchEnd={() => setPressed(false)}
      >
        {/* 光沢スライドエフェクト */}
        <span
          className="absolute inset-0 pointer-events-none rounded-full"
          style={{
            background:
              'linear-gradient(105deg, transparent 35%, rgba(255,255,255,0.45) 50%, transparent 65%)',
            backgroundSize: '250% 100%',
            backgroundPosition: hovered ? '-100% 0' : '200% 0',
            transition: 'background-position 0.55s ease',
          }}
        />
        <i
          className="ri-rocket-2-line text-xl relative z-10"
          style={{ color: variant === 'white' ? '#e85a2a' : '#fff' }}
        ></i>
        <span className="relative z-10">今すぐLINE追加で+2包GET！</span>
      </a>
      <style>{`
        @keyframes ctaPulse {
          0%   { box-shadow: 0 0 0 0 ${variant === 'white' ? 'rgba(255,255,255,0.6)' : 'rgba(232,90,42,0.5)'}; }
          70%  { box-shadow: 0 0 0 14px rgba(0,0,0,0); }
          100% { box-shadow: 0 0 0 0 rgba(0,0,0,0); }
        }
      `}</style>
    </div>
  );
}

// 注釈コンポーネント
function Disclaimer({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[10px] text-gray-400 leading-relaxed mt-1">{children}</p>
  );
}

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const s1 = useInView();
  const s2 = useInView();
  const s3 = useInView();
  const s4 = useInView();
  const s5 = useInView();

  return (
    <div className="min-h-screen bg-white font-sans" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>

      {/* ── ファーストビュー ── */}
      <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/5c4e954b10a7bceb088303ed1ca9e5f9.jpg"
            alt=""
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/50 to-white/90" />
        </div>

        <div className="relative z-10 w-full max-w-sm mx-auto px-6 pt-20 pb-12 flex flex-col items-center text-center">
          <div className="mb-6 inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-[#b8a96a]/40 rounded-full px-4 py-1.5 shadow-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-[#e85a2a] inline-block animate-pulse"></span>
            <span className="text-[10px] tracking-widest text-[#e85a2a] font-medium uppercase">CAMPFIRE にて公開中！</span>
          </div>

          <div className="mb-5">
            <img
              src="https://static.readdy.ai/image/6d1552f340f8b16d5538e9b5dc05368f/4f1d6d7bad92488d1276d2951db9d591.png"
              alt="enme"
              className="h-14 w-auto mx-auto"
            />
          </div>

          <h1 className="text-[2rem] font-black text-gray-900 leading-tight mb-3 tracking-tight">
            自販機の水が、<br />
            <span className="relative inline-block">
              <span className="relative z-10 text-[#3d7a3d]">5秒で「飲むサラダ」</span>
              <span className="absolute bottom-1 left-0 w-full h-2 bg-[#c8e6c8]/60 -z-0 rounded"></span>
            </span>
            に。
          </h1>

          <p className="text-sm text-gray-600 leading-relaxed mb-7 mt-3">
            忙しい私を満たす、新発想のモバイル酵素習慣<br />
            <strong className="text-gray-800">enme（エンミー）</strong>
          </p>

          <div className="mt-2 relative w-full mb-3">
            <div className="absolute -inset-4 bg-gradient-to-b from-[#e8f5e8]/60 to-transparent rounded-full blur-2xl"></div>
            <img
              src="https://static.readdy.ai/image/6d1552f340f8b16d5538e9b5dc05368f/5e8cc1fa5b2f39b4086d33f30e06cb72.png"
              alt="enme VITAMIN C1000 ENZYME パッケージ"
              className="relative w-full max-w-sm h-auto object-contain mx-auto drop-shadow-2xl"
            />
          </div>

          <p className="text-xs font-black text-[#b8a96a] text-center mb-2 tracking-wide">＼ 【先着40名限定】一番お得な最大30%OFF ／</p>
          <LineCtaButton variant="white" />
          <p className="text-[11px] text-gray-500 text-center mt-2 leading-relaxed px-1">{LINE_NOTE}</p>
          <Disclaimer>※「飲むサラダ」は商品の特徴を表現したキャッチコピーであり、野菜・サラダの代替を保証するものではありません。</Disclaimer>

        </div>
      </section>

      <CurvedDivider fromColor="#ffffff" toColor="#e8f4f8" />

      {/* ── 課題喚起 ── */}
      <section className="pb-0 bg-[#e8f4f8]">
        <div
          ref={s1.ref}
          className={`max-w-sm mx-auto px-5 pt-10 transition-all duration-700 ${s1.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* タイトル */}
          <h2 className="text-xl font-black text-center mb-1 leading-snug" style={{ color: '#2a4a6a' }}>
            こんな毎日、
            <span className="relative inline-block mx-1">
              <span className="relative z-10" style={{ color: '#c8a000' }}>送っていませんか？</span>
              <span className="absolute bottom-0 left-0 w-full h-0.5 rounded" style={{ background: '#c8a000' }}></span>
            </span>
          </h2>
          <p className="text-center text-xs text-gray-500 mb-6">「わかってるけど、できない」が続いてる...</p>

          {/* 中央レイアウト：吹き出し＋人物 */}
          <div className="relative mt-2" style={{ minHeight: '340px' }}>

            {/* 上段 左吹き出し */}
            <div className="absolute top-0 left-0 w-36 z-10">
              <div className="bg-white border-2 border-dashed border-[#a8c8d8] rounded-2xl px-3 py-2.5 shadow-sm relative">
                <p className="text-[11px] font-bold text-gray-700 leading-snug">お腹がゆるかったり<br />張ったり…安定しない</p>
                <div className="absolute -bottom-2.5 right-5 w-0 h-0"
                  style={{ borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderTop: '10px solid white' }}></div>
              </div>
            </div>

            {/* 上段 右吹き出し */}
            <div className="absolute top-0 right-0 w-32 z-10">
              <div className="bg-white border-2 border-dashed border-[#a8c8d8] rounded-2xl px-3 py-2.5 shadow-sm relative">
                <p className="text-[11px] font-bold text-gray-700 leading-snug">肌がくすんで<br />透明感がない</p>
                <div className="absolute -bottom-2.5 left-5 w-0 h-0"
                  style={{ borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderTop: '10px solid white' }}></div>
              </div>
            </div>

            {/* 中央 人物画像 */}
            <div className="absolute inset-x-0 top-10 flex justify-center z-0">
              <div className="relative">
                <div className="absolute inset-0 rounded-full blur-3xl scale-110"
                  style={{ background: 'radial-gradient(ellipse, rgba(200,230,240,0.7) 0%, rgba(232,244,248,0.3) 70%, transparent 100%)' }}></div>
                <img
                  src="https://public.readdy.ai/ai/img_res/f92a0a1f-380e-4133-80a6-1a7b19dae4d2.png"
                  alt="お悩みの女性"
                  className="relative w-40 h-auto object-contain object-top drop-shadow-md"
                />
              </div>
            </div>

            {/* 中段 左吹き出し */}
            <div className="absolute left-0 z-10" style={{ top: '145px' }}>
              <div className="w-34 bg-white border-2 border-dashed border-[#a8c8d8] rounded-2xl px-3 py-2.5 shadow-sm relative" style={{ width: '128px' }}>
                <p className="text-[11px] font-bold text-gray-700 leading-snug">野菜、今週<br />食べてないかも…</p>
                <div className="absolute -right-2.5 top-1/2 -translate-y-1/2 w-0 h-0"
                  style={{ borderTop: '6px solid transparent', borderBottom: '6px solid transparent', borderLeft: '10px solid white' }}></div>
              </div>
            </div>

            {/* 中段 右吹き出し */}
            <div className="absolute right-0 z-10" style={{ top: '145px' }}>
              <div className="w-32 bg-white border-2 border-dashed border-[#a8c8d8] rounded-2xl px-3 py-2.5 shadow-sm relative">
                <p className="text-[11px] font-bold text-gray-700 leading-snug">サプリ、買っても<br />飲み忘れる</p>
                <div className="absolute -left-2.5 top-1/2 -translate-y-1/2 w-0 h-0"
                  style={{ borderTop: '6px solid transparent', borderBottom: '6px solid transparent', borderRight: '10px solid white' }}></div>
              </div>
            </div>

            {/* スペーサー */}
            <div style={{ height: '320px' }}></div>
          </div>

          {/* まとめバナー */}
          <div className="relative -mt-4 mb-0 mx-2">
            <div className="bg-white rounded-3xl px-5 py-5 text-center shadow-md border border-[#c8e0e8]/60">
              <p className="text-xs text-gray-500 mb-1">「気をつけなきゃ」と思いながらも</p>
              <p className="text-lg font-black leading-snug" style={{ color: '#2a4a6a' }}>
                結局、今日も<span style={{ color: '#c8a000' }}>何もできなかった…</span>
              </p>
            </div>
            <div className="flex justify-center mt-3">
              <div className="w-0 h-0"
                style={{ borderLeft: '14px solid transparent', borderRight: '14px solid transparent', borderTop: '16px solid #a8c8d8' }}></div>
            </div>
          </div>
        </div>

        {/* 共感カード */}
        <div className="max-w-sm mx-auto px-5 pt-5 pb-10">
          <div className="space-y-3">
            {[
              {
                icon: 'ri-goblet-line',
                color: '#b8a96a',
                bg: '#fdf8ec',
                label: '食事の乱れ',
                text: 'コンビニ・外食続きで「今日も野菜ゼロ」。でも明日も忙しい。',
              },
              {
                icon: 'ri-drop-line',
                color: '#c47a7a',
                bg: '#fdf0f0',
                label: '肌・体の変化',
                text: '肌のくすみが気になる。ビタミンCが大事とわかっていても、続かない。',
              },
              {
                icon: 'ri-capsule-line',
                color: '#5a8a5a',
                bg: '#f0f7f0',
                label: 'サプリ疲れ',
                text: '「続けよう」と買ったサプリが棚の奥に。錠剤を毎日飲む習慣、しんどい。',
              },
              {
                icon: 'ri-mental-health-line',
                color: '#7a7ac4',
                bg: '#f0f0fd',
                label: '腸の不調',
                text: 'なんとなくお腹が重い日が続く。腸活、何から始めればいいかわからない。',
              },
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-4 bg-white rounded-2xl px-5 py-4 shadow-sm border border-white/80"
              >
                <div className="flex-shrink-0">
                  <div
                    className="w-10 h-10 flex items-center justify-center rounded-xl"
                    style={{ background: item.bg }}
                  >
                    <i className={`${item.icon} text-xl`} style={{ color: item.color }}></i>
                  </div>
                  <p className="text-[9px] font-bold text-center mt-1" style={{ color: item.color }}>{item.label}</p>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed pt-1">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CurvedDivider fromColor="#e8f4f8" toColor="#ffffff" flip />

      {/* ── 解決策 ── */}
      <section className="py-20 px-6 bg-white">
        <div
          ref={s2.ref}
          className={`max-w-sm mx-auto transition-all duration-700 ${s2.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <p className="text-[10px] tracking-widest text-[#5a8a5a] font-medium uppercase text-center mb-3">Solution</p>
          <h2 className="text-2xl font-black text-gray-900 text-center mb-2 leading-snug">
            その悩み、enmeの<br />
            <span className="text-[#3d7a3d]">「ちびだら飲み」</span>新習慣が<br />解決します！
          </h2>
          <p className="text-xs text-gray-500 text-center mb-10">忙しいあなたに寄り添う、4つの特徴</p>

          {[
            {
              num: '01',
              accent: '#b8a96a',
              accentBg: '#fdf8ec',
              border: '#e8ddb0',
              title: '圧倒的な手軽さ',
              img: 'https://public.readdy.ai/ai/img_res/edited_80f7494d9f40195888aaefb32a5eab49_6b25cb43.jpg',
              imgAlt: '水に溶かすだけ',
              body: 'ペットボトルの水（500ml）にサッと溶かすだけ。5秒で完成。シェイカー不要、外出先でも使える。',
              note: null,
            },
            {
              num: '02',
              accent: '#3d7a3d',
              accentBg: '#f0f7f0',
              border: '#b8d8b8',
              title: 'タイムリリース処方',
              img: 'https://readdy.ai/api/search-image?query=Minimalist%20flat%20lay%20of%20a%20clear%20water%20bottle%20with%20lemon%20slices%20and%20a%20delicate%20clock%20illustration%2C%20soft%20green%20botanical%20leaves%2C%20white%20background%2C%20slow%20release%20vitamin%20concept%2C%20elegant%20wellness%20photography%2C%20muted%20sage%20and%20ivory%20palette&width=800&height=500&seq=feat2_v3&orientation=landscape',
              imgAlt: '2時間かけてゆっくり',
              body: '2時間かけてゆっくり飲むことで、ビタミンCを持続補給。間食の欲求も自然にブロック。',
              note: '※「点滴のように」は飲み方の特徴を表現したものであり、医療行為の効果を示すものではありません。間食抑制の効果には個人差があります。',
            },
            {
              num: '03',
              accent: '#b8a96a',
              accentBg: '#fdf8ec',
              border: '#e8ddb0',
              title: '圧倒的な栄養',
              img: 'https://readdy.ai/api/search-image?query=Artistic%20arrangement%20of%20fifty%20fresh%20lemons%20with%20probiotic%20capsules%20and%20green%20fiber%20powder%20on%20white%20marble%2C%20vibrant%20yellow%20and%20sage%20green%2C%20luxury%20nutrition%20photography%2C%20editorial%20food%20styling%2C%20clean%20and%20sophisticated%20composition&width=800&height=500&seq=feat3_v3&orientation=landscape',
              imgAlt: '豊富な栄養素',
              body: null,
              note: '※ビタミンC 1,000mgはレモン果汁のビタミンC含有量（約20mg/個）との比較表現です。効果には個人差があります。',
              nutrients: [
                { icon: 'ri-sun-line', color: '#b8a96a', bg: '#fdf8ec', label: 'レモン50個分のビタミンC（1,000mg）' },
                { icon: 'ri-heart-pulse-line', color: '#3d7a3d', bg: '#f0f7f0', label: '乳酸菌100億個' },
                { icon: 'ri-leaf-line', color: '#5a8a5a', bg: '#edf5ed', label: '水溶性食物繊維2,400mg' },
                { icon: 'ri-flask-line', color: '#b8a96a', bg: '#fdf8ec', label: '100種類以上の酵素を凝縮' },
              ],
            },
          ].map((feat, i) => (
            <div
              key={i}
              className="mb-6 rounded-3xl overflow-hidden shadow-md border"
              style={{ borderColor: feat.border }}
            >
              <div className="relative">
                <img src={feat.img} alt={feat.imgAlt} className="w-full h-44 object-cover object-top" />
                <div
                  className="absolute top-4 left-4 w-9 h-9 flex items-center justify-center rounded-full text-white text-xs font-black shadow"
                  style={{ background: feat.accent }}
                >
                  {feat.num}
                </div>
              </div>
              <div className="px-5 py-5" style={{ background: feat.accentBg }}>
                <h3 className="text-base font-black text-gray-900 mb-3">{feat.title}</h3>
                {feat.body && (
                  <p className="text-sm text-gray-700 leading-relaxed">{feat.body}</p>
                )}
                {feat.nutrients && (
                  <div className="space-y-2">
                    {feat.nutrients.map((n, j) => (
                      <div key={j} className="flex items-center gap-3 bg-white rounded-xl px-3 py-2.5 shadow-sm">
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: n.bg }}>
                          <i className={`${n.icon} text-base`} style={{ color: n.color }}></i>
                        </div>
                        <p className="text-xs text-gray-800 font-medium">{n.label}</p>
                      </div>
                    ))}
                  </div>
                )}
                {feat.note && <Disclaimer>{feat.note}</Disclaimer>}
              </div>
            </div>
          ))}

          {/* 04 食事代わり・ダイエット提案カード */}
          <div className="mb-6 rounded-3xl overflow-hidden shadow-md border" style={{ borderColor: '#c8d8b0' }}>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Elegant%20slim%20waist%20woman%20holding%20a%20clear%20water%20bottle%20with%20green%20powder%20drink%2C%20soft%20morning%20light%2C%20minimalist%20white%20studio%20background%2C%20healthy%20diet%20lifestyle%20concept%2C%20botanical%20green%20leaves%20accent%2C%20clean%20beauty%20wellness%20photography%2C%20muted%20sage%20green%20and%20ivory%20tones%2C%20editorial%20fashion%20style&width=800&height=500&seq=diet_feat_v1&orientation=landscape"
                alt="食事代わりにもなるダイエット習慣"
                className="w-full h-44 object-cover object-top"
              />
              <div
                className="absolute top-4 left-4 w-9 h-9 flex items-center justify-center rounded-full text-white text-xs font-black shadow"
                style={{ background: '#5a8a5a' }}
              >
                04
              </div>
              <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 shadow-sm">
                <span className="text-[10px] font-black text-[#3d7a3d] tracking-wide">NEW HABIT</span>
              </div>
            </div>
            <div className="px-5 py-5" style={{ background: '#f2f7ec' }}>
              <h3 className="text-base font-black text-gray-900 mb-1">食事代わりにもなる、<br />自然なダイエット習慣</h3>
              <p className="text-[10px] text-[#5a8a5a] font-bold mb-3 tracking-wide uppercase">Meal Replacement &amp; Diet Support</p>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                含まれる<strong className="text-gray-900">水溶性食物繊維「イヌリン」</strong>が胃腸で膨らみ、自然な満腹感をキープ。忙しい朝や軽めのランチの<strong className="text-gray-900">食事代わり</strong>にも。
              </p>

              <div className="space-y-2 mb-4">
                {[
                  { icon: 'ri-expand-up-down-line', color: '#5a8a5a', bg: '#e8f5e0', label: '満腹感を持続', sub: '間食を自然にブロック' },
                  { icon: 'ri-restaurant-line', color: '#b8a96a', bg: '#fdf8ec', label: '朝・ランチの置き換えに', sub: '水に溶かすだけで栄養補給' },
                  { icon: 'ri-scales-3-line', color: '#3d7a3d', bg: '#f0f7f0', label: '無理なく続けられる', sub: '我慢ゼロの新習慣' },
                ].map((item, j) => (
                  <div key={j} className="flex items-center gap-3 bg-white rounded-xl px-3 py-2.5 shadow-sm">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: item.bg }}>
                      <i className={`${item.icon} text-base`} style={{ color: item.color }}></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-800 font-bold leading-tight">{item.label}</p>
                      <p className="text-[10px] text-gray-500 leading-tight">{item.sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-gradient-to-r from-[#3d7a3d]/10 to-[#b8a96a]/10 border border-[#b8d8b8] rounded-2xl px-4 py-3 flex items-start gap-3">
                <div className="w-7 h-7 flex items-center justify-center bg-[#3d7a3d] rounded-full flex-shrink-0 mt-0.5">
                  <i className="ri-information-line text-white text-xs"></i>
                </div>
                <p className="text-[11px] text-gray-700 leading-relaxed">
                  イヌリンは<strong className="text-gray-900">2,400mg配合</strong>。食前に飲むことで血糖値の急上昇を抑え、腸内環境も整えます。
                </p>
              </div>
              <Disclaimer>※血糖値・腸内環境への影響には個人差があります。血糖値に関する疾患をお持ちの方は医師にご相談ください。</Disclaimer>
            </div>
          </div>
        </div>
      </section>

      <CurvedDivider fromColor="#ffffff" toColor="#fafaf8" />

      {/* ── 中間CTA ── */}
      <section className="py-10 px-6 bg-[#fafaf8]">
        <div className="max-w-sm mx-auto">
          <div className="bg-gradient-to-br from-[#3d7a3d] to-[#5a8a5a] rounded-3xl px-6 py-7 text-center shadow-xl relative overflow-hidden">
            <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/10"></div>
            <div className="absolute -bottom-6 -left-6 w-24 h-24 rounded-full bg-white/10"></div>
            <div className="relative z-10">
              <p className="text-[10px] tracking-widest text-[#c8e6c8] font-bold uppercase mb-2">Now on CAMPFIRE</p>
              <p className="text-white font-black text-base leading-snug mb-1">
                クラウドファンディング公開中！
              </p>
              <p className="text-white/80 text-xs leading-relaxed mb-3">
                毎日の「飲むサラダ」習慣を、一番お得な価格で始めませんか？
              </p>
              <p className="text-[#f9d71c] text-xs font-black mb-4">＼ 【先着40名限定】一番お得な最大30%OFF ／</p>
              <LineCtaButton variant="white" />
              <p className="text-white/70 text-[11px] text-center mt-2 leading-relaxed">{LINE_NOTE}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── 信頼性 ── */}
      <section className="py-20 px-6 bg-[#fafaf8]">
        <div
          ref={s3.ref}
          className={`max-w-sm mx-auto transition-all duration-700 ${s3.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <p className="text-[10px] tracking-widest text-[#5a8a5a] font-medium uppercase text-center mb-3">Trust & Authority</p>
          <h2 className="text-2xl font-black text-gray-900 text-center mb-10 leading-snug">
            創業70余年の<br />発酵技術が支える<br />確かな品質
          </h2>

          <div className="bg-white rounded-3xl overflow-hidden shadow-md border border-[#e8ddb0]">
            <img
              src="https://readdy.ai/api/search-image?query=Traditional%20Japanese%20artisan%20fermentation%20workshop%20with%20wooden%20barrels%20and%20modern%20stainless%20equipment%2C%20warm%20amber%20lighting%2C%20heritage%20craftsmanship%20meets%20contemporary%20manufacturing%2C%20editorial%20industrial%20photography%2C%20sophisticated%20and%20trustworthy%20atmosphere%2C%20muted%20earth%20tones&width=800&height=500&seq=factory_v3&orientation=landscape"
              alt="製造工場"
              className="w-full h-48 object-cover object-top"
            />
            <div className="px-5 py-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center bg-[#fdf8ec] rounded-lg">
                  <i className="ri-building-line text-base text-[#b8a96a]"></i>
                </div>
                <span className="text-xs font-bold text-[#b8a96a] tracking-wide uppercase">老舗メーカーの技術</span>
              </div>
              <p className="text-sm font-black text-gray-900 mb-1">創業70余年の発酵技術</p>
              <p className="text-xs text-[#b8a96a] font-medium mb-3">日本で初めて酵素飲料を開発</p>
              <p className="text-xs text-gray-600 leading-relaxed">
                伝統の発酵技術で、酵素独特の匂いを抑えた<strong className="text-gray-800">美味しいレモン風味</strong>を実現。毎日続けられる味にこだわりました。
              </p>
            </div>
          </div>
        </div>
      </section>

      <CurvedDivider fromColor="#fafaf8" toColor="#ffffff" flip />

      {/* ── ブランドストーリー ── */}
      <section className="py-20 px-6 bg-white overflow-hidden">
        <div className="max-w-sm mx-auto">
          <p className="text-[10px] tracking-widest text-[#5a8a5a] font-medium uppercase text-center mb-3">Brand Story</p>
          <h2 className="text-2xl font-black text-gray-900 text-center mb-2 leading-snug">
            「忙しい人を、<br />
            <span className="text-[#3d7a3d]">サラダで満たしたい。</span>」
          </h2>
          <p className="text-xs text-gray-500 text-center mb-8 leading-relaxed">
            大阪発・サラダ専門店が生んだ、<br />新しい栄養習慣の形。
          </p>

          <div className="relative rounded-3xl overflow-hidden shadow-lg mb-4">
            <img
              src="https://static.readdy.ai/image/6d1552f340f8b16d5538e9b5dc05368f/d696728a20f92126f112d40f8b1468b7.jpeg"
              alt="大阪のサラダ専門店"
              className="w-full h-64 object-cover object-top"
            />
          </div>
          <div className="flex items-center gap-1.5 mb-8">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-map-pin-line text-[#3d7a3d] text-xs"></i>
            </div>
            <span className="text-[11px] font-bold text-[#3d7a3d] tracking-wide">大阪発 — サラダ専門店</span>
            <div className="flex-1 h-px bg-[#b8d8b8] ml-1"></div>
          </div>

          <div className="space-y-5 mb-8">
            {[
              { icon: 'ri-store-2-line', color: '#3d7a3d', bg: '#f0f7f0', title: '大阪のサラダ専門店から生まれた', body: '「サラダで満腹への挑戦」をコンセプトに、忙しい方々に愛されてきた大阪のサラダ専門店。お店に来られない方にも同じ想いを届けたい——そんな願いからenmeは誕生しました。' },
              { icon: 'ri-user-heart-line', color: '#b8a96a', bg: '#fdf8ec', title: '「Enrich Me」—— 私を豊かに', body: 'enmeの名前は「Enrich Me（私を豊かにする）」から。移動中でも仕事中でも、手軽に体の内側から満たされる新しい栄養習慣を提案します。' },
              { icon: 'ri-seedling-line', color: '#5a8a5a', bg: '#f0f7f0', title: 'お店の「生きた栄養」を、1包に', body: '野菜・果実・発酵の力を凝縮したenmeは、まさにサラダ専門店のノウハウを詰め込んだ1包。あなたの毎日に寄り添います。' },
            ].map((item, i) => (
              <div key={i} className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-xl mt-0.5" style={{ background: item.bg }}>
                  <i className={`${item.icon} text-base`} style={{ color: item.color }}></i>
                </div>
                <div>
                  <p className="text-sm font-black text-gray-900 mb-1">{item.title}</p>
                  <p className="text-xs text-gray-600 leading-relaxed">{item.body}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="relative bg-gradient-to-br from-[#f0f7f0] to-[#fdf8ec] rounded-3xl px-6 py-6 border border-[#b8d8b8] shadow-sm">
            <div className="absolute -top-3 left-6 w-6 h-6 flex items-center justify-center bg-[#3d7a3d] rounded-full shadow">
              <i className="ri-double-quotes-l text-white text-xs"></i>
            </div>
            <p className="text-sm text-gray-800 font-medium leading-relaxed italic mt-2">
              「お店に来られない日も、あなたの体を満たしたい。<br />
              それがenmeを作った、私たちの想いです。」
            </p>
            <p className="text-[10px] text-[#5a8a5a] font-bold mt-3 tracking-wide">— enme 開発チーム / 大阪サラダ専門店</p>
          </div>
        </div>
      </section>

      <CurvedDivider fromColor="#ffffff" toColor="#fdf8ec" />

      {/* ── YouTube ショート動画 ── */}
      <section className="py-16 px-6 bg-[#fdf8ec]">
        <div className="max-w-sm mx-auto">
          <p className="text-[10px] tracking-widest text-[#5a8a5a] font-medium uppercase text-center mb-3">Movie</p>
          <h2 className="text-xl font-black text-gray-900 text-center mb-2 leading-snug">
            動画で見る、<span className="text-[#3d7a3d]">enmeの使い方</span>
          </h2>
          <p className="text-xs text-gray-500 text-center mb-6">イメージソングにのせた、enmeのCMをご覧ください。</p>
          <div className="relative w-full rounded-3xl overflow-hidden shadow-lg border border-[#e8ddb0]" style={{ paddingTop: '177.78%' }}>
            <iframe
              className="absolute inset-0 w-full h-full"
              src="https://www.youtube.com/embed/BINtZWta-yg"
              title="enme 使い方動画"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      </section>

      <CurvedDivider fromColor="#fdf8ec" toColor="#fafaf8" flip />

      {/* ── 成分インフォグラフィック ── */}
      <section className="py-16 px-6 bg-[#fafaf8] overflow-hidden">
        <div className="max-w-sm mx-auto">
          <p className="text-[10px] tracking-widest text-[#5a8a5a] font-medium uppercase text-center mb-3">Ingredients</p>
          <h2 className="text-2xl font-black text-gray-900 text-center mb-2 leading-snug">
            レモン50個分のビタミンCを、<br />
            <span className="text-[#3d7a3d]">この1包に凝縮。</span>
          </h2>
          <p className="text-xs text-gray-500 text-center mb-2">まるで「飲む点滴」。必要な栄養素を、美味しくチャージ。</p>
          <Disclaimer>※「レモン50個分」はレモン果汁のビタミンC含有量（約20mg/個）との比較表現です。「飲む点滴」は商品特性を表現したキャッチコピーであり、医療行為の効果を示すものではありません。</Disclaimer>
          <div className="mb-6 mt-6">

            <div className="relative mb-6">
              <div className="flex justify-center mb-4">
                <div className="relative">
                  <div className="absolute -inset-6 bg-gradient-to-b from-[#e8f5e8]/80 to-transparent rounded-full blur-2xl"></div>
                  <img
                    src="https://static.readdy.ai/image/6d1552f340f8b16d5538e9b5dc05368f/5e8cc1fa5b2f39b4086d33f30e06cb72.png"
                    alt="enme パッケージ"
                    className="relative w-64 h-auto object-contain drop-shadow-xl mx-auto"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-gradient-to-br from-[#fffde7] to-[#fff9c4] border-2 border-[#f9d71c]/40 rounded-3xl px-3 py-4 text-center shadow-md relative overflow-hidden">
                  <div className="absolute -top-3 -right-3 w-12 h-12 bg-[#f9d71c]/20 rounded-full"></div>
                  <div className="w-8 h-8 flex items-center justify-center bg-[#f9d71c]/30 rounded-full mx-auto mb-2">
                    <i className="ri-sun-line text-base text-[#c8a000]"></i>
                  </div>
                  <p className="text-[9px] font-bold text-[#8a6a00] mb-0.5">ビタミンC</p>
                  <p className="text-xl font-black text-[#c8a000] leading-none">1,000</p>
                  <p className="text-[9px] font-bold text-[#c8a000]">mg</p>
                  <p className="text-[8px] text-[#8a6a00] mt-1">レモン50個分</p>
                </div>

                <div className="bg-gradient-to-br from-[#f9fbe7] to-[#f0f4c3] border-2 border-[#aed581]/40 rounded-3xl px-3 py-4 text-center shadow-md relative overflow-hidden">
                  <div className="absolute -top-3 -left-3 w-12 h-12 bg-[#aed581]/20 rounded-full"></div>
                  <div className="w-8 h-8 flex items-center justify-center bg-[#aed581]/30 rounded-full mx-auto mb-2">
                    <i className="ri-leaf-line text-base text-[#558b2f]"></i>
                  </div>
                  <p className="text-[9px] font-bold text-[#33691e] mb-0.5 leading-tight">水溶性食物繊維<br/>イヌリン</p>
                  <p className="text-xl font-black text-[#558b2f] leading-none">2,400</p>
                  <p className="text-[9px] font-bold text-[#558b2f]">mg</p>
                </div>

                <div className="bg-gradient-to-br from-[#f1f8e9] to-[#dcedc8] border-2 border-[#81c784]/40 rounded-3xl px-3 py-4 text-center shadow-md relative overflow-hidden">
                  <div className="absolute -bottom-3 -right-3 w-12 h-12 bg-[#81c784]/20 rounded-full"></div>
                  <div className="w-8 h-8 flex items-center justify-center bg-[#81c784]/30 rounded-full mx-auto mb-2">
                    <i className="ri-heart-pulse-line text-base text-[#2e7d32]"></i>
                  </div>
                  <p className="text-[9px] font-bold text-[#1b5e20] mb-0.5">乳酸菌</p>
                  <p className="text-xl font-black text-[#2e7d32] leading-none">100億</p>
                  <p className="text-[9px] font-bold text-[#2e7d32]">個</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-white border border-[#f9d71c]/30 rounded-2xl px-4 py-3 flex items-center gap-3 shadow-sm">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#f9d71c]/20 rounded-lg flex-shrink-0">
                    <i className="ri-flask-line text-sm text-[#c8a000]"></i>
                  </div>
                  <div>
                    <p className="text-[9px] text-[#8a6a00] font-bold leading-tight">さらに</p>
                    <p className="text-xs font-black text-gray-800 leading-tight">100種類以上の<br/>国産野菜・果実の<br/>発酵エキス配合</p>
                  </div>
                </div>
                <div className="bg-white border border-[#81c784]/30 rounded-2xl px-4 py-3 flex items-center gap-3 shadow-sm">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#81c784]/20 rounded-lg flex-shrink-0">
                    <i className="ri-star-line text-sm text-[#2e7d32]"></i>
                  </div>
                  <div>
                    <p className="text-[9px] text-[#1b5e20] font-bold leading-tight">ターゲット要望No.1</p>
                    <p className="text-xs font-black text-gray-800 leading-tight">「美肌・ビタミン」<br/>に特化した<br/>スペック</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-[#3d7a3d] to-[#5a8a5a] rounded-2xl px-5 py-5 text-center shadow-lg">
                <p className="text-white font-black text-sm leading-relaxed">
                  まるで「飲む点滴」。<br />
                  必要な栄養素を、美味しくチャージ。
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#fdf8ec] to-[#f5f9f0] border-2 border-[#b8a96a]/30 rounded-3xl px-5 py-5 text-center shadow-md">
              <p className="text-[10px] tracking-widest text-[#8a7a3a] font-bold uppercase mb-2">Price</p>
              <p className="text-sm text-gray-600 mb-1">クラウドファンディング最安値</p>
              <div className="flex items-end justify-center gap-1 mb-2">
                <span className="text-3xl font-black text-[#3d7a3d]">約250円</span>
                <span className="text-sm text-gray-500 mb-1">〜 / 1包</span>
              </div>
              <p className="text-[10px] text-[#b8a96a] font-bold">※超早割適用時の最安値価格</p>
              <Disclaimer>※クラウドファンディング超早割（先着40名）適用時の参考価格。</Disclaimer>
            </div>
          </div>
        </div>
      </section>

      <CurvedDivider fromColor="#fafaf8" toColor="#3d7a3d" flip />

      {/* ── 数値実績バー ── */}
      <section className="py-16 px-6 bg-[#3d7a3d] overflow-hidden relative">
        <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
          <div className="absolute -top-10 -left-10 w-48 h-48 rounded-full bg-white/5"></div>
          <div className="absolute -bottom-16 -right-8 w-64 h-64 rounded-full bg-white/5"></div>
        </div>

        <div
          ref={s4.ref}
          className={`max-w-sm mx-auto relative z-10 transition-all duration-700 ${s4.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <p className="text-[10px] tracking-widest text-[#c8e6c8] font-medium uppercase text-center mb-2">Numbers</p>
          <h2 className="text-xl font-black text-white text-center mb-8 leading-snug">
            数字で見る、enmeの実力
          </h2>

          <div className="grid grid-cols-2 gap-4">
            {[
              { emoji: '🍋', iconClass: 'ri-sun-line', iconColor: '#f9d71c', iconBg: 'rgba(249,215,28,0.2)', value: '1,000', unit: 'mg', label: 'ビタミンC', sub: 'レモン50個分', barColor: 'rgba(249,215,28,0.6)', barW: 'w-full' },
              { emoji: '🫧', iconClass: 'ri-heart-pulse-line', iconColor: '#81c784', iconBg: 'rgba(129,199,132,0.2)', value: '100億', unit: '個', label: '乳酸菌', sub: '1包あたり', barColor: 'rgba(129,199,132,0.6)', barW: 'w-4/5' },
              { emoji: '🥦', iconClass: 'ri-leaf-line', iconColor: '#aed581', iconBg: 'rgba(174,213,129,0.2)', value: '100', unit: '+', label: '種類の酵素', sub: '凝縮配合', barColor: 'rgba(174,213,129,0.6)', barW: 'w-3/4' },
              { emoji: '🏺', iconClass: 'ri-award-line', iconColor: '#ffcc80', iconBg: 'rgba(255,204,128,0.2)', value: '70', unit: '年+', label: '発酵の歴史', sub: '老舗メーカー', barColor: 'rgba(255,204,128,0.6)', barW: 'w-2/3' },
            ].map((item, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-3xl px-4 py-5 border border-white/20 relative overflow-hidden">
                <div className="absolute -bottom-4 -right-4 opacity-10 text-7xl leading-none select-none">{item.emoji}</div>
                <div className="w-9 h-9 flex items-center justify-center rounded-xl mb-3" style={{ background: item.iconBg }}>
                  <i className={`${item.iconClass} text-lg`} style={{ color: item.iconColor }}></i>
                </div>
                <p className="text-2xl font-black text-white leading-none mb-0.5">{item.value}<span className="text-sm font-bold">{item.unit}</span></p>
                <p className="text-xs text-white/80 font-bold mb-0.5">{item.label}</p>
                <p className="text-[10px] text-white/50 leading-tight">{item.sub}</p>
                <div className="mt-3 h-1 bg-white/10 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${item.barW}`} style={{ background: item.barColor }}></div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 bg-white/10 border border-white/20 rounded-2xl px-5 py-4 flex items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center bg-white/15 rounded-xl flex-shrink-0">
              <i className="ri-flask-line text-xl text-[#f9d71c]"></i>
            </div>
            <p className="text-xs text-white/80 leading-relaxed">
              <strong className="text-white">国産野菜・果実100種類以上</strong>の発酵エキスを凝縮。<br />
              まるで「飲む点滴」の栄養密度。
            </p>
          </div>
        </div>
      </section>

      <CurvedDivider fromColor="#3d7a3d" toColor="#ffffff" flip />

      {/* ── クロージング CTA ── */}
      <section id="cta" className="py-20 px-6 bg-white">
        <div
          ref={s5.ref}
          className={`max-w-sm mx-auto transition-all duration-700 ${s5.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <p className="text-[10px] tracking-widest text-[#5a8a5a] font-medium uppercase text-center mb-3">Early Access</p>
          <h2 className="text-2xl font-black text-gray-900 text-center mb-2 leading-snug">
            習慣化するなら、<br />一番お得な<span className="text-[#3d7a3d]">「今」</span>がチャンス！
          </h2>
          <p className="text-xs text-gray-500 text-center mb-8 leading-relaxed">
            クラウドファンディング公開中！<br />
            <strong className="text-[#b8a96a]">最大30%OFFの超早割</strong>は先着40名限定です。
          </p>

          <div className="bg-gradient-to-br from-[#f5f9f0] to-[#fdf8ec] rounded-3xl px-5 py-5 mb-8 border border-[#d8e8d0]">
            <p className="text-xs font-black text-gray-800 mb-4 text-center tracking-wide">LINE登録で受け取れる特典</p>
            <ul className="space-y-3">
              {[
                '最大30%OFFの超早割購入権（先着40名）',
                '登録後に届く合言葉で、購入時に2包プレゼント',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center bg-[#3d7a3d] rounded-full flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-white text-xs"></i>
                  </div>
                  <span className="text-sm text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="text-xs text-gray-500 text-center mb-8 leading-relaxed">
            超早割は枠が埋まり次第終了となります。<br />
            お見逃しなく！
          </p>

          <p className="text-xs font-black text-[#b8a96a] text-center mb-3 tracking-wide">＼ 【先着40名限定】一番お得な最大30%OFF ／</p>
          <LineCtaButton variant="white" />
          <p className="text-[11px] text-gray-500 text-center mt-2 leading-relaxed px-1">{LINE_NOTE}</p>

        </div>
      </section>

      {/* ── フッター ── */}
      <footer className="border-t border-[#d8e8d0] py-10 px-6" style={{ background: '#f0f7f0' }}>
        <div className="max-w-sm mx-auto text-center">
          <img
            src="https://static.readdy.ai/image/6d1552f340f8b16d5538e9b5dc05368f/4f1d6d7bad92488d1276d2951db9d591.png"
            alt="enme"
            className="h-10 w-auto mx-auto mb-3 opacity-70"
          />
          <p className="text-xs text-gray-500 leading-relaxed mb-4">
            忙しい現代人のための、新しい酵素習慣。<br />enme（エンミー）
          </p>

          {/* 景表法・薬機法 免責事項 */}
          <div className="bg-white/60 border border-[#c8d8c8] rounded-2xl px-4 py-4 mb-5 text-left">
            <p className="text-[10px] font-bold text-gray-500 mb-2 tracking-wide">【免責事項】</p>
            <ul className="space-y-1.5 text-[10px] text-gray-400 leading-relaxed list-none">
              <li>・本品は食品（栄養補助食品）であり、医薬品ではありません。</li>
              <li>・疾病の診断・治療・予防を目的とするものではありません。</li>
              <li>・「飲むサラダ」「飲む点滴」は商品特性を表現したキャッチコピーです。</li>
              <li>・「レモン50個分」はビタミンC含有量（約20mg/個）との比較表現です。</li>
              <li>・効果・効能には個人差があります。</li>

              <li>・妊娠中・授乳中・治療中の方は医師にご相談ください。</li>
              <li>・価格は超早割（先着40名）適用時の参考価格です。</li>
              <li>・本ページは景品表示法・薬機法を遵守した表現を使用しています。</li>
            </ul>
          </div>

          <p className="text-xs text-gray-400">© 2025 enme. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
